import { FROM_LOCATION_MAP } from '@shared/utils/experimentNavHelpers';
import { Location } from 'history';
import { useCallback, useMemo } from 'react';
import { generatePath, useHistory, useLocation, useParams } from 'react-router';

const labelMap = {
  [FROM_LOCATION_MAP.EXPERIMENT_TABLE]: 'Return to experiments',
  [FROM_LOCATION_MAP.ARCHIVE]: 'Return to archive',
  [FROM_LOCATION_MAP.PANELS]: 'Return to project',
  default: 'Return to project'
};

const routeMap = {
  experiments: 'Return to experiments',
  archive: 'Return to archive',
  panels: 'Return to project'
} as const;

type RouteMapKey = keyof typeof routeMap;

const useBackToProjectRoute = () => {
  const history = useHistory();
  const { state } = useLocation<{
    from: string;
    prevLocation: Location;
  }>();
  const params = useParams<{
    projectName: string;
    workspace: string;
  }>();

  const queryParams = new URLSearchParams(window.location.search);
  const prevLocationQueryProp: string | null = queryParams.get('prevPath');

  const fromPanel = prevLocationQueryProp?.split('/').pop();

  const returnToProjectLabel = useMemo(() => {
    return (
      labelMap[state?.from] ||
      (fromPanel && fromPanel in routeMap
        ? routeMap[fromPanel as RouteMapKey]
        : undefined) ||
      labelMap.default
    );
  }, [state?.from, fromPanel]);

  const onReturnToProject = useCallback(() => {
    if (state?.prevLocation) {
      return history.push(state.prevLocation);
    }

    if (!state?.prevLocation && prevLocationQueryProp) {
      return history.push(prevLocationQueryProp);
    }

    return history.push(generatePath('/:workspace/:projectName', params));
  }, [state, prevLocationQueryProp, history, params]);

  return {
    returnToProjectLabel,
    onReturnToProject
  };
};

export default useBackToProjectRoute;
